/**
 * The disguise: what the app looks like before it admits to being Arcanum.
 *
 * Everything here is per device and lives in localStorage, for the same reason
 * the app lock does — a disguise is a property of the phone in your pocket, not
 * of the account. It is also the reason the whole thing can never lock anyone
 * out: if this state is missing, for any reason at all, the app simply opens
 * normally. iOS evicts a PWA's storage after a stretch of disuse, and a member
 * who forgot nothing must not end up facing a calculator they cannot get past.
 *
 * Forgetting the key deliberately has no recovery flow, and needs none: delete
 * the app from the home screen and add it again. Nothing is lost — the account,
 * the messages, the vault and the member number all live on the server, and the
 * installed app is a bookmark with a picture on it.
 */

const MODE_KEY = 'arca.decoy.mode';
const KEY_KEY = 'arca.decoy.key';
// Deliberately NOT the app lock's timestamp. Both would listen for the same
// visibility events, and the lock clears its stamp when it decides the absence
// was short -- so whichever handler ran second would read a value the other had
// already erased. Two owners, two keys, no race.
const AWAY_KEY = 'arca.decoy.awaySince';

/** Away longer than this and the app returns to its disguise rather than to
 *  the lock screen. Members write in short bursts with long gaps, so this
 *  lands at the start of a conversation and not in the middle of one. */
export const COLD_AFTER_MS = 60 * 60 * 1000;

const read = (k) => {
  try {
    return localStorage.getItem(k);
  } catch (e) {
    return null;   // private mode, blocked storage: no disguise, no lockout
  }
};

const write = (k, v) => {
  try {
    if (v === null) localStorage.removeItem(k);
    else localStorage.setItem(k, v);
  } catch (e) { /* the disguise is a convenience; never fail loudly */ }
};

/** 'calculator' — or null, which means the app looks like itself. */
export function decoyMode() {
  const m = read(MODE_KEY);
  // DISGUISES is declared further down, beside applyDisguiseChrome, because
  // the two belong together. Safe: this body runs long after the module has
  // finished evaluating. Reading the table rather than naming the modes here
  // is the point -- a new disguise is one entry, not two places to remember.
  return DISGUISES[m] ? m : null;
}

/** The sequence that opens it, exactly as typed: e.g. "7×8". */
export function decoyKey() {
  const k = read(KEY_KEY);
  return k && k.length ? k : null;
}

export function setDecoy(mode, key) {
  if (!mode || !key) {
    write(MODE_KEY, null);
    write(KEY_KEY, null);
    return;
  }
  write(MODE_KEY, mode);
  write(KEY_KEY, key);
}

export function clearDecoy() {
  setDecoy(null, null);
}

/**
 * Is the disguise currently in force?
 *
 * Both halves must be present. A mode with no key would be a calculator with
 * no way past it, which is the one failure this feature must not have.
 */
export function decoyArmed() {
  return !!decoyMode() && !!decoyKey();
}

/**
 * Should the disguise be showing right now?
 *
 * True on a cold start, and after a long absence. A short absence is the app
 * lock's business: asking for a password is quicker than solving a puzzle, and
 * a disguise that appears every time you glance at a message gets switched off
 * within a week.
 *
 * `cold` is passed rather than inferred, because only the caller knows whether
 * this is the first render of a fresh page load.
 */
export function shouldShowDecoy(cold) {
  if (!decoyArmed()) return false;
  if (cold) return true;
  const t = parseInt(read(AWAY_KEY) || '', 10);
  return Number.isFinite(t) && Date.now() - t > COLD_AFTER_MS;
}

/** Does what was typed open it? Compared as a sequence, not a result — the
 *  point is that the member remembers a calculation, not a number. */
export function decoyKeyMatches(typed) {
  const secret = decoyKey();
  return !!secret && typed === secret;
}

/** Note when the app left the foreground, so a long absence can be told from
 *  a short one on the way back. */
export function markDecoyAway() {
  write(AWAY_KEY, String(Date.now()));
}

export function clearDecoyAway() {
  write(AWAY_KEY, null);
}

/**
 * The three disguises, and what each one tells the phone about itself.
 *
 * Only the calculator is built. The others are listed so that adding one is
 * filling in a row rather than rediscovering what a row needs.
 */
export const DISGUISES = {
  calculator: {
    label: 'Lommeregner',
    manifest: '/disguise/calculator/manifest.json',
    appleIcon: '/disguise/calculator/apple-touch-icon.png',
    favicon: '/disguise/calculator/icon-192.png',
    themeColor: '#1C1C1E',
  },
  clock: {
    label: 'Ur',
    manifest: '/disguise/clock/manifest.json',
    appleIcon: '/disguise/clock/apple-touch-icon.png',
    favicon: '/disguise/clock/icon-192.png',
    themeColor: '#0C0C0E',
  },
  converter: {
    label: 'Omregner',
    manifest: '/disguise/converter/manifest.json',
    appleIcon: '/disguise/converter/apple-touch-icon.png',
    favicon: '/disguise/converter/icon-192.png',
    themeColor: '#24303A',
  },
};

/**
 * Point the page's own metadata at a disguise, or back at Arcanum.
 *
 * This has to happen BEFORE the app is added to the home screen: a PWA's icon
 * and name are copied at that moment and there is no API to change them
 * afterwards. Signal manages it in settings only because Signal is native.
 * Changing disguise later means removing the app and adding it again.
 *
 * The document title matters as much as the icon. A calculator on the home
 * screen whose tab says "Arcanum" has hidden nothing.
 *
 * THIS IS THE RUNTIME HALF ONLY -- it applies a disguise chosen while the app
 * is open. On every later page load the markup arrives as Arcanum again, so
 * public/index.html carries a small script that does the same thing before the
 * browser reads the manifest. A new disguise has to be added in both places.
 */
export function applyDisguiseChrome(mode) {
  if (typeof document === 'undefined') return;
  const d = DISGUISES[mode] || null;

  // Every match, not the first. index.html may carry more than one tag for the
  // same thing, and a disguise applied to half of them is not applied.
  const set = (selector, attr, value, create) => {
    let els = Array.from(document.querySelectorAll(selector));
    if (!els.length && create) {
      const el = create();
      document.head.appendChild(el);
      els = [el];
    }
    els.forEach((el) => el.setAttribute(attr, value));
  };

  set('link[rel="manifest"]', 'href', d ? d.manifest : '/manifest.json');
  set('link[rel="icon"]', 'href', d ? d.favicon : '/favicon.ico',
    () => Object.assign(document.createElement('link'), { rel: 'icon' }));
  set('link[rel="apple-touch-icon"]', 'href', d ? d.appleIcon : '/apple-touch-icon.png',
    () => Object.assign(document.createElement('link'), { rel: 'apple-touch-icon' }));
  set('meta[name="apple-mobile-web-app-title"]', 'content', d ? d.label : 'Arcanum',
    () => Object.assign(document.createElement('meta'), { name: 'apple-mobile-web-app-title' }));
  set('meta[name="theme-color"]', 'content', d ? d.themeColor : '#07090A',
    () => Object.assign(document.createElement('meta'), { name: 'theme-color' }));

  document.title = d ? d.label : 'Arcanum';
}
